import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/button';
import { VolumeIcon, SparklesIcon, StopCircleIcon } from 'lucide-react';
import { ScrollArea } from '@/components/scroll-area';
import ReactMarkdown from 'react-markdown';
import { useTypingAnimation } from '@/hooks/useTypingAnimation';
import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Message } from '@/types/mesage';

interface MessageListProps {
  messages: Message[];
  scrollAreaRef: React.RefObject<HTMLDivElement>;
  speakMessage: (message: Message) => void;
  stopSpeaking: () => void;
  isSpeaking: string | null;
  isThinking: boolean;
}

export function MessageList({
  messages,
  scrollAreaRef,
  speakMessage,
  stopSpeaking,
  isSpeaking,
  isThinking,
}: MessageListProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = useState(true);

  const scrollToBottom = useCallback(() => {
    if (autoScroll) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [autoScroll]);

  const handleScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    const element = event.currentTarget;
    const isScrolledNearBottom =
      element.scrollHeight - element.scrollTop - element.clientHeight < 100;
    setAutoScroll(isScrolledNearBottom);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const isSpeakingMessage = useCallback(
    (messageId: string) => {
      return messageId == isSpeaking;
    },
    [messages, isSpeaking]
  );

  return (
    <ScrollArea
      className="flex-grow mb-6 pr-4"
      ref={scrollAreaRef}
      onScroll={handleScroll}
    >
      <AnimatePresence>
        {messages.map((message, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className={`mb-4 ${message.role === 'user' ? 'text-right' : 'text-left'}`}
          >
            <div
              className={`inline-block max-w-[80%] p-3 rounded-lg ${
                message.role === 'user'
                  ? 'bg-[#4A9BFF] text-white'
                  : 'bg-[#2C2C2C] text-white'
              }`}
            >
              <div className="w-full text-xl">
                {message.role === 'user' ? (
                  message.content
                ) : (
                  <TypingMessage content={message.content} />
                )}
              </div>
              {message.role === 'assistant' && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    isSpeakingMessage(message.id!)
                      ? stopSpeaking()
                      : speakMessage(message)
                  }
                  className="ml-2 text-[#4A9BFF] hover:text-[#3A7ACC]"
                >
                  {isSpeakingMessage(message.id!) ? (
                    <StopCircleIcon className="h-4 w-4" />
                  ) : (
                    <VolumeIcon className="h-4 w-4" />
                  )}
                </Button>
              )}
            </div>
          </motion.div>
        ))}
        {messages.length > 0 && isThinking && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex items-center space-x-2 text-xl text-[#4A9BFF] mt-4"
          >
            <SparklesIcon className="h-5 w-5 animate-pulse" />
            <span>I'm thinking fam hang on...</span>
          </motion.div>
        )}
      </AnimatePresence>
      <div ref={messagesEndRef} />
    </ScrollArea>
  );
}

function TypingMessage({ content }: { content: string }) {
  const displayedText = useTypingAnimation(content);
  const isTypingComplete = displayedText.length === content.length;

  useEffect(() => {
    if (!isTypingComplete) {
      const element = document.querySelector('[data-scroll-anchor]');
      element?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [displayedText, isTypingComplete]);

  return (
    <div
      className="w-full"
      style={{ minHeight: `${content.split('\n').length * 1.5}em` }}
    >
      <ReactMarkdown
        components={{
          a: ({ node, ...props }) => (
            <a className="text-blue-400 !text-lg hover:underline" {...props} />
          ),
        }}
      >
        {displayedText}
      </ReactMarkdown>
      {!isTypingComplete && (
        <span className="inline-block text-lg w-1 h-4 ml-1 bg-white animate-blink"></span>
      )}
    </div>
  );
}
